/**
 * metaLeadGen/deviceBreakdown.ts
 *
 * Aggregates Meta rows by real device type (iOS / Android / Desktop).
 * Shows only real Meta data — spend, optins, clicks, CTR, cost per optin.
 * Application/close attribution by device will be added once UTM tracking is in place.
 */

import type { MetaLeadGenRow, MetaLeadGenFilters } from "./types";
import { applyFilters } from "./filters";
import { safeDivide } from "./calculations";

// ─────────────────────────────────────────────
// DEVICE CONSTANTS
// ─────────────────────────────────────────────
export const DEVICES = ["ios", "android", "desktop"] as const;
export type DeviceKey = (typeof DEVICES)[number];

// ─────────────────────────────────────────────
// OUTPUT TYPE
// ─────────────────────────────────────────────
export interface DeviceDailyRow {
  date: string; // display format e.g. "Jan 13"

  ios_spend:     number; android_spend:   number; desktop_spend:   number;
  ios_clicks:    number; android_clicks:  number; desktop_clicks:  number;
  ios_optins:    number; android_optins:  number; desktop_optins:  number;

  ios_ctr:       number | null; android_ctr:     number | null; desktop_ctr:     number | null;
  ios_cpo:       number | null; android_cpo:     number | null; desktop_cpo:     number | null;
}

// ─────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────
function toDisplayDate(isoDate: string): string {
  const d = new Date(isoDate + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

interface DeviceAcc {
  spend: number; clicks: number; impressions: number; leads: number;
}

function emptyAcc(): DeviceAcc { return { spend: 0, clicks: 0, impressions: 0, leads: 0 }; }

type DateAcc = Record<DeviceKey, DeviceAcc>;

function emptyDateAcc(): DateAcc {
  return { ios: emptyAcc(), android: emptyAcc(), desktop: emptyAcc() };
}

function nullIfZero(n: number): number | null { return n === 0 ? null : n; }

function buildDailyRow(date: string, acc: DateAcc): DeviceDailyRow {
  const build = (key: DeviceKey) => {
    const a = acc[key];
    return {
      spend:   a.spend,
      clicks:  a.clicks,
      optins:  a.leads,
      ctr:     nullIfZero(a.impressions) !== null ? safeDivide(a.clicks, a.impressions) * 100 : null,
      cpo:     nullIfZero(a.leads)       !== null ? safeDivide(a.spend,  a.leads)              : null,
    };
  };
  const i = build("ios");
  const a = build("android");
  const d = build("desktop");
  return {
    date,
    ios_spend:     i.spend,   android_spend:   a.spend,   desktop_spend:   d.spend,
    ios_clicks:    i.clicks,  android_clicks:  a.clicks,  desktop_clicks:  d.clicks,
    ios_optins:    i.optins,  android_optins:  a.optins,  desktop_optins:  d.optins,
    ios_ctr:       i.ctr,     android_ctr:     a.ctr,     desktop_ctr:     d.ctr,
    ios_cpo:       i.cpo,     android_cpo:     a.cpo,     desktop_cpo:     d.cpo,
  };
}

// ─────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────
export function getDeviceDailyData(
  rows: MetaLeadGenRow[],
  filters: MetaLeadGenFilters
): DeviceDailyRow[] {
  const filtered = applyFilters(rows, filters);

  const dateSet = new Set<string>();
  for (const row of filtered) dateSet.add(row.date);
  const dates = Array.from(dateSet).sort();

  const byDate = new Map<string, DateAcc>();
  for (const date of dates) byDate.set(date, emptyDateAcc());

  for (const row of filtered) {
    const acc  = byDate.get(row.date)!;
    const key: DeviceKey = (DEVICES as readonly string[]).includes(row.device)
      ? (row.device as DeviceKey)
      : "ios";
    acc[key].spend       += row.spend;
    acc[key].clicks      += row.clicks;
    acc[key].impressions += row.impressions;
    acc[key].leads       += row.leads;
  }

  return dates.map(date => buildDailyRow(toDisplayDate(date), byDate.get(date)!));
}
